import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useAuth } from "../../Context/AuthProvider";
import useSnackbar from "../../Utils/useSnackbar";
import { tokens, withAlpha } from "../../Common/designTokens";
import { deleteWatchlist } from "../../http/api";

const ConfirmWatchlistDeleteDialog = ({ open, onClose, watchlist }) => {
  const { token } = useAuth();
  const { showError, showSuccess } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (!open) {
      setIsLoading(false);
      setLocalError("");
    }
  }, [open]);

  const handleConfirmDelete = async () => {
    if (!watchlist?.watchlistId || !token) {
      return;
    }

    setLocalError("");
    setIsLoading(true);

    try {
      await deleteWatchlist(token, watchlist.watchlistId);
      showSuccess("Watchlist deleted successfully");
      onClose(true);
    } catch (error) {
      const message = error?.message || "Failed to delete watchlist";
      setLocalError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setLocalError("");
      onClose(false);
    }
  };

  if (!watchlist) return null;

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 450,
    bgcolor: tokens.colors.bodyBackgroundColor,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: 24,
    p: 4,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  return (
    <Modal
      open={open}
      onClose={!isLoading ? handleClose : undefined}
      aria-labelledby="confirm-watchlist-delete-modal"
    >
      <Box sx={modalStyle}>
        <Typography
          id="confirm-watchlist-delete-modal-title"
          variant="h6"
          component="h2"
          sx={{
            color: tokens.colors.fontColour,
            mb: 2,
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          Confirm Delete Watchlist
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <WarningAmberIcon sx={{ fontSize: 48, color: tokens.palette.danger }} />
        </Box>

        {localError ? (
          <Box
            sx={{
              width: "100%",
              mb: 2,
              p: 1,
              backgroundColor: withAlpha(tokens.palette.danger, 0.1),
              border: `1px solid ${withAlpha(tokens.palette.danger, 0.4)}`,
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ color: tokens.palette.danger, textAlign: "center", fontWeight: 700 }}>
              {localError}
            </Typography>
          </Box>
        ) : null}

        <Typography
          variant="subtitle1"
          sx={{ color: tokens.colors.fontColour85, textAlign: "center", mb: 2 }}
        >
          Are you sure you want to delete this watchlist?
        </Typography>

        <Typography
          variant="body2"
          sx={{ mb: 1, color: tokens.colors.fontColour, textAlign: "center" }}
        >
          Watchlist: {watchlist.watchlistName || "Unknown"}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: tokens.palette.danger, mb: 3, textAlign: "center", fontWeight: 600 }}
        >
          This action cannot be undone.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 120,
              borderRadius: "8px",
              "&.Mui-disabled": {
                color: tokens.colors.disabledFontColour,
                borderColor: tokens.colors.bodyBorderColor50,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleConfirmDelete}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              width: 140,
              color: tokens.colors.white,
              borderColor: tokens.palette.danger,
              backgroundColor: withAlpha(tokens.palette.danger, 0.95),
              borderRadius: "8px",
              "&:hover": { backgroundColor: withAlpha(tokens.palette.danger, 1) },
              "&.Mui-disabled": {
                color: withAlpha(tokens.colors.white, 0.4),
                borderColor: withAlpha(tokens.colors.white, 0.4),
              },
            }}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmWatchlistDeleteDialog;
